import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { normalize, schema } from "normalizr";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { TRACKER_FILTER_OPTIONS, TRACKER_STATUS } from "modules/Tracker/constants";
import { statusTypeSelector } from "services/StatusType/selectors";
import {
    getCountTracks,
    getCountTracksError,
    getCountTracksSuccess,
    getTrackerProgressInfo,
    getTracks,
    getTracksError,
    getTracksSuccess,
    setLoadersOnTasks,
    setTrackerProgressInfo,
    deleteTracks,
    setSelectedTrackerTasks,
    getTrackFullMessage,
    setTrackFullMessage
} from "modules/Tracker/services/Tracks/actions";
import {
    getTrackProgressInfoRequest,
    getTemplateRequest,
    getTracksRequest,
    getNoticeByTrackIdRequest,
    templateCountRequest,
    getTrackWithOldestTimeRequest,
    getCountTracksRequest,
    deleteTrackRequest,
    getTrackFullMessageRequest
} from "modules/Tracker/services/Tracks/api";
import { tracksSelector } from "modules/Tracker/services/Tracks/selectors";
import messageHelper from "utils/messages/messageHelper";
import {ITEMS_PER_PAGE} from "constants/general";
import {sidParamsSelector} from "modules/App/state/selectors";

const scrapeTasksSchema = new schema.Entity(
    "status_types", undefined , { idAttribute: (entity) => entity.track_id.track_id }
);
const scrapeTasksListSchema = new schema.Array(scrapeTasksSchema);

//Workers
function* handleRefreshScrapeTasks() {
    yield put(setSelectedTrackerTasks([]));
    const sidParams = yield select(sidParamsSelector);
    yield put(getTracks({ limit: ITEMS_PER_PAGE, ...sidParams }));
}

export function* getTracksWorker({ payload }) {
    try {
        const searchDefault = Object.keys(payload).length ? payload : TRACKER_FILTER_OPTIONS;
        const searchParams = { enable_wildcards: false };

        Object.keys(searchDefault).forEach((key)=>{
            let value = searchDefault[key];
            if (key === "browser_source_type_code" && value === "_") {
                value = "";
            }
            if (value || String(value) === "0" || String(value) === "false" || key === "browser_source_type_code") {
                searchParams[key] = value;
            }
        });

        const { scrape_tasks } = yield call(getTracksRequest, searchParams);
        const { result, entities } = normalize(scrape_tasks, scrapeTasksListSchema);
        yield put(getTracksSuccess({
            isLoading: false,
            scrapeTasks: entities.status_types,
            ids: result,
            selectedTasks: []
        }));
    } catch (error) {
        yield put(getTracksError());

        getErrorMessage(error);
    }
}

export function* getStatusInfo({ codes, id, storage_type_code }) {
    return yield all([
        ...codes.map(code => call(getTrackProgressInfoRequest, { code, id, storage_type_code }))
    ]);
}

export function* getTemplateCodeCount({ templates, id, storage_type_code }) {
    return yield all([
        ...templates.map(template =>
            call(templateCountRequest, { code: template || "NO_DATA", id, storage_type_code })
        )
    ]);
}

function calculateSuccessRatePercent (statusInfo) {
    const Done = statusInfo.find((item) => item.code === TRACKER_STATUS.DONE).count;
    const Warning = statusInfo.find((item) => item.code === TRACKER_STATUS.WARNING).count;
    const Error = statusInfo.find((item) => item.code === TRACKER_STATUS.ERROR).count;

    return  Math.floor(100 * (Done) / (Done + Warning + Error));
}

export function* creatingNewInfoWorker(data) {
    try {
        const { id, searchParams } = data;
        const newSearchParams = { storage_type_code: TRACKER_FILTER_OPTIONS.storage_type_code ,...searchParams };
        const { codes } = yield select(statusTypeSelector);
        const { scrapeTasks } = yield select(tracksSelector);

        const newProgressInfo = {};
        newProgressInfo[id] = {};
        //check status info
        const statusInfo = yield call(getStatusInfo, { codes, id, storage_type_code: newSearchParams.storage_type_code });
        newProgressInfo[id].statusInfo = statusInfo;
        //check template ARR
        const { templates } = yield call(getTemplateRequest, { id, storage_type_code: newSearchParams.storage_type_code });
        newProgressInfo[id].templateInfo = templates;
        //Get Notice
        newProgressInfo[id].notice = yield call(getNoticeByTrackIdRequest, id);
        //Check template count if exists
        newProgressInfo[id].templateCodeCount = yield call(getTemplateCodeCount,{ templates, id, storage_type_code: newSearchParams.storage_type_code });

        newProgressInfo[id].successRate = {};
        const successRatePercent = calculateSuccessRatePercent(statusInfo);
        newProgressInfo[id].successRate.successRatePercent = successRatePercent;
        newProgressInfo[id].allCount = statusInfo.reduce((acc, item) => acc + item.count, 0);
        newProgressInfo[id].startDate = scrapeTasks[id].created_at_timestamp;

        if (successRatePercent !== 0) {
            newProgressInfo[ id ].successRate.trackWithOldestTime =
                yield call(getTrackWithOldestTimeRequest, { id, storage_type_code: newSearchParams.storage_type_code });
        }

        return newProgressInfo;
    } catch (error) {
        getErrorMessage(error);
    }
}

export function* creatingNewFullMessageWorker(data) {
    try {
        const { trackID, initialTrackID, searchParams } = data;
        const newSearchParams = { storage_type_code: TRACKER_FILTER_OPTIONS.storage_type_code ,...searchParams };

        const newFullMessages = {};
        newFullMessages[trackID] = {};
        const { message } = yield call(getTrackFullMessageRequest, { track_id: trackID, initial_track_id: initialTrackID, storage_type_code: newSearchParams.storage_type_code });
        newFullMessages[trackID] = message;

        return newFullMessages;
    } catch (error) {
        getErrorMessage(error);
    }
}

export function* handleGetTrackerProgressInfo({ payload }) {
    try {
        yield put(setLoadersOnTasks(payload.id));

        const { progressInfo } = yield select(tracksSelector);

        const newProgressInfo = { ...progressInfo, ...yield call(creatingNewInfoWorker, payload) };
        yield put(setTrackerProgressInfo(newProgressInfo));

        yield put(setLoadersOnTasks(payload.id));
    } catch (error) {
        yield put(setLoadersOnTasks(payload.id));
        getErrorMessage(error);
    }
}

export function* handleGetTrackFullMessage({ payload }) {
    try {
        yield put(setLoadersOnTasks(payload.trackID));

        const { fullMessages } = yield select(tracksSelector);

        const newFullMessages = { ...fullMessages, ...yield call(creatingNewFullMessageWorker, payload) };
        yield put(setTrackFullMessage(newFullMessages));

        yield put(setLoadersOnTasks(payload.trackID));
    } catch (error) {
        yield put(setLoadersOnTasks(payload.trackID));
        getErrorMessage(error);
    }
}

function* handleGetCountTracks ({ payload }) {
    try {
        const { count } = yield call(getCountTracksRequest, payload);
        yield put(getCountTracksSuccess(count));
    } catch (error) {
        getErrorMessage(error);
        yield put(getCountTracksError());
    }
}

function* deleteTracksWorker({ payload }) {
    try {
        yield all(payload.map((id) => call(deleteTrackRequest, id)));
        yield handleRefreshScrapeTasks();

        messageHelper({
            type: "success",
            actionSubject: `Task${payload.length > 1 ? "s" : ""} with id${payload.length > 1 ? "s" : ""} - ${payload.join(", ")}`,
            action: "deleted",
            plural: payload.length > 1
        });
    } catch (error) {
        getErrorMessage(error);
    }
}

// Watchers
export function* rootSagaTracks() {
    yield all([
        yield takeLatest(getTracks, getTracksWorker),
        yield takeEvery(getTrackerProgressInfo, handleGetTrackerProgressInfo),
        yield takeEvery(getTrackFullMessage, handleGetTrackFullMessage),
        yield takeLatest(getCountTracks, handleGetCountTracks),
        yield takeLatest(deleteTracks, deleteTracksWorker)
    ]);
}
